import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import StatusPill from '../../components/ui/StatusPill';
import EmptyState from '../../components/ui/EmptyState';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import PageHeader from '../../components/ui/PageHeader';
import Button from '../../components/ui/Button';
import { toast } from '../../components/ui/Toast';
import { useTranslation } from '../../i18n/LanguageContext';
import {
  IconTool,
  IconFileAlert,
  IconAlertTriangle,
  IconSos,
  IconSearch,
  IconDownload,
  IconPhone,
} from '@tabler/icons-react';

export default function Safety() {
  const { language } = useTranslation();
  const { orgId, userProfile, role } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('defects');
  const [defects, setDefects] = useState([]);
  const [nearMisses, setNearMisses] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [emergencies, setEmergencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const siteId = userProfile?.siteId;

  useEffect(() => {
    const load = async () => {
      if (!orgId) return;
      try {
        const [defSnap, nmSnap, incSnap, emSnap] = await Promise.all([
          getDocs(query(collection(db, 'organisations', orgId, 'defects'), orderBy('reportedAt', 'desc'))),
          getDocs(query(collection(db, 'organisations', orgId, 'nearMisses'), orderBy('reportedAt', 'desc'))),
          getDocs(query(collection(db, 'organisations', orgId, 'incidents'), orderBy('detectedAt', 'desc'))),
          getDocs(query(collection(db, 'organisations', orgId, 'emergencies'), orderBy('triggeredAt', 'desc'))),
        ]);
        const filter = (arr) => role === 'manager' && siteId ? arr.filter(x => x.siteId === siteId) : arr;
        setDefects(filter(defSnap.docs.map(d => ({ id: d.id, ...d.data() }))));
        setNearMisses(filter(nmSnap.docs.map(d => ({ id: d.id, ...d.data() }))));
        setIncidents(filter(incSnap.docs.map(d => ({ id: d.id, ...d.data() }))));
        setEmergencies(emSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [orgId, siteId, role]);

  const formatDateTime = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString(language, { day: 'numeric', month: 'short' }) + ' · ' + d.toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit' });
  };

  const triggerLabel = (t) => {
    if (t === 'impact') return 'Impact';
    if (t === 'manual') return 'Manual SOS';
    if (t === 'man_down') return 'Man-down';
    if (t === 'no_movement') return 'No movement';
    return t || '—';
  };

  const openDefects = defects.filter(d => d.status === 'open').length;
  const openNearMisses = nearMisses.filter(n => n.status === 'open').length;
  const activeIncidents = incidents.filter(i => i.status === 'active' || i.status === 'open').length;
  const activeEmergencies = emergencies.filter(e => e.status === 'active').length;

  const TABS = [
    { key: 'defects',     label: 'Defects',     icon: IconTool,          count: defects.length,     alert: openDefects     },
    { key: 'nearMisses',  label: 'Near misses', icon: IconFileAlert,     count: nearMisses.length,  alert: openNearMisses  },
    { key: 'incidents',   label: 'Incidents',   icon: IconAlertTriangle, count: incidents.length,   alert: activeIncidents },
    { key: 'emergencies', label: 'Emergencies', icon: IconSos,           count: emergencies.length, alert: activeEmergencies },
  ];

  const searchMatch = (text) => !search || text?.toLowerCase().includes(search.toLowerCase());

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <PageHeader
          title="Safety"
          subtitle={(openDefects + openNearMisses + activeIncidents + activeEmergencies) + ' open issue' + ((openDefects + openNearMisses + activeIncidents + activeEmergencies) !== 1 ? 's' : '')}
          actions={
            activeTab === 'emergencies' ? (
              <Button variant="secondary" icon={<IconDownload size={14} stroke={1.5} />} onClick={() => toast.info('HSE export coming soon')}>Export for HSE</Button>
            ) : null
          }
        />

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 2, marginBottom: 20, borderBottom: '0.5px solid var(--color-border-light)' }}>
          {TABS.map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.key;
            return (
              <div
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 7,
                  padding: '10px 16px', fontSize: 13, cursor: 'pointer',
                  color: active ? 'var(--color-brand)' : 'var(--color-text-secondary)',
                  borderBottom: active ? '2px solid var(--color-brand)' : '2px solid transparent',
                  marginBottom: -1, transition: 'all 0.15s',
                }}
              >
                <Icon size={14} stroke={1.5} />
                {tab.label}
                <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{tab.count}</span>
                {tab.alert > 0 && (
                  <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 20, background: 'var(--color-danger)', color: '#fff' }}>{tab.alert}</span>
                )}
              </div>
            );
          })}
        </div>

        {/* Search */}
        <div style={{ position: 'relative', marginBottom: 16, maxWidth: 400 }}>
          <IconSearch size={14} stroke={1.5} color="var(--color-text-tertiary)" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
          <input type="text" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} style={{ paddingLeft: 32, width: '100%', boxSizing: 'border-box' }} />
        </div>

        {/* DEFECTS TAB */}
        {activeTab === 'defects' && (
          <Card padding="0">
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 0.8fr 0.8fr 80px', padding: '8px 16px', borderBottom: '0.5px solid var(--color-border-light)', background: 'var(--color-bg-secondary)' }}>
              {['Description', 'Reported by', 'Severity', 'Status', ''].map(h => (
                <div key={h} style={{ fontSize: 10, color: 'var(--color-text-tertiary)', letterSpacing: '0.04em' }}>{h}</div>
              ))}
            </div>
            {loading ? <LoadingSkeleton rows={4} /> : defects.filter(d => searchMatch(d.description) || searchMatch(d.reportedBy)).length === 0 ? (
              <EmptyState icon={<IconTool size={32} stroke={1.5} />} title="No defects" message="No defects reported" />
            ) : defects.filter(d => searchMatch(d.description) || searchMatch(d.reportedBy)).map((defect, i, arr) => (
              <div key={defect.id} onClick={() => navigate('/defects/' + defect.id)} style={{
                display: 'grid', gridTemplateColumns: '2fr 1.5fr 0.8fr 0.8fr 80px',
                padding: '12px 16px', borderBottom: i < arr.length - 1 ? '0.5px solid var(--color-border-light)' : 'none',
                cursor: 'pointer', alignItems: 'center',
                background: defect.severity === 'critical' ? 'var(--color-danger-light)' : 'transparent',
              }}>
                <div>
                  <div style={{ fontSize: 13, color: defect.severity === 'critical' ? 'var(--color-danger)' : 'var(--color-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{defect.description || 'No description'}</div>
                  <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 1 }}>{defect.machineName || '—'} · {formatDateTime(defect.reportedAt)}</div>
                </div>
                <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{defect.reportedBy || '—'}</div>
                <StatusPill status={defect.severity || 'minor'} />
                <StatusPill status={defect.status || 'open'} />
                <div style={{ fontSize: 12, color: 'var(--color-brand)', textAlign: 'right' }}>View</div>
              </div>
            ))}
          </Card>
        )}

        {/* NEAR MISSES TAB */}
        {activeTab === 'nearMisses' && (
          <Card padding="0">
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 80px', padding: '8px 16px', borderBottom: '0.5px solid var(--color-border-light)', background: 'var(--color-bg-secondary)' }}>
              {['Description', 'Reported by', 'Status', ''].map(h => (
                <div key={h} style={{ fontSize: 10, color: 'var(--color-text-tertiary)', letterSpacing: '0.04em' }}>{h}</div>
              ))}
            </div>
            {loading ? <LoadingSkeleton rows={4} /> : nearMisses.filter(n => searchMatch(n.description) || searchMatch(n.reportedBy)).length === 0 ? (
              <EmptyState icon={<IconFileAlert size={32} stroke={1.5} />} title="No near misses" message="No near misses reported" />
            ) : nearMisses.filter(n => searchMatch(n.description) || searchMatch(n.reportedBy)).map((nm, i, arr) => (
              <div key={nm.id} onClick={() => navigate('/near-misses/' + nm.id)} style={{
                display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 80px',
                padding: '12px 16px', borderBottom: i < arr.length - 1 ? '0.5px solid var(--color-border-light)' : 'none',
                cursor: 'pointer', alignItems: 'center',
              }}>
                <div>
                  <div style={{ fontSize: 13, color: 'var(--color-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nm.description || 'No description'}</div>
                  <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 1 }}>{formatDateTime(nm.reportedAt)}</div>
                </div>
                <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{nm.reportedBy || '—'}</div>
                <StatusPill status={nm.status || 'open'} />
                <div style={{ fontSize: 12, color: 'var(--color-brand)', textAlign: 'right' }}>View</div>
              </div>
            ))}
          </Card>
        )}

        {/* INCIDENTS TAB */}
        {activeTab === 'incidents' && (
          <Card padding="0">
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 0.8fr 80px', padding: '8px 16px', borderBottom: '0.5px solid var(--color-border-light)', background: 'var(--color-bg-secondary)' }}>
              {['Operator', 'Date & time', 'Type', 'Status', ''].map(h => (
                <div key={h} style={{ fontSize: 10, color: 'var(--color-text-tertiary)', letterSpacing: '0.04em' }}>{h}</div>
              ))}
            </div>
            {loading ? <LoadingSkeleton rows={4} /> : incidents.filter(i => searchMatch(i.triggeredByName) || searchMatch(i.machineName)).length === 0 ? (
              <EmptyState icon={<IconAlertTriangle size={32} stroke={1.5} />} title="No incidents" message="No incidents recorded" />
            ) : incidents.filter(i => searchMatch(i.triggeredByName) || searchMatch(i.machineName)).map((inc, i, arr) => (
              <div key={inc.id} onClick={() => navigate('/incidents/' + inc.id)} style={{
                display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 0.8fr 80px',
                padding: '12px 16px', borderBottom: i < arr.length - 1 ? '0.5px solid var(--color-border-light)' : 'none',
                cursor: 'pointer', alignItems: 'center',
                background: inc.status === 'active' ? 'var(--color-danger-light)' : 'transparent',
              }}>
                <div>
                  <div style={{ fontSize: 13, color: inc.status === 'active' ? 'var(--color-danger)' : 'var(--color-text-primary)' }}>{inc.triggeredByName || '—'}</div>
                  <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 1 }}>{inc.machineName || '—'}</div>
                </div>
                <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{formatDateTime(inc.detectedAt)}</div>
                <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{triggerLabel(inc.triggerType)}</div>
                <StatusPill status={inc.status || 'open'} />
                <div style={{ fontSize: 12, color: 'var(--color-brand)', textAlign: 'right' }}>View</div>
              </div>
            ))}
          </Card>
        )}

        {/* EMERGENCIES TAB */}
        {activeTab === 'emergencies' && (
          <Card padding="0">
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 0.8fr 0.8fr 60px', padding: '8px 16px', borderBottom: '0.5px solid var(--color-border-light)', background: 'var(--color-bg-secondary)' }}>
              {['Operator', 'Date & time', 'Trigger', '999', 'Status', ''].map(h => (
                <div key={h} style={{ fontSize: 10, color: 'var(--color-text-tertiary)', letterSpacing: '0.04em' }}>{h}</div>
              ))}
            </div>
            {loading ? <LoadingSkeleton rows={4} /> : emergencies.filter(e => searchMatch(e.triggeredByName)).length === 0 ? (
              <EmptyState icon={<IconSos size={32} stroke={1.5} />} title="No emergencies" message="No emergencies recorded" />
            ) : emergencies.filter(e => searchMatch(e.triggeredByName)).map((em, i, arr) => (
              <div key={em.id} onClick={() => navigate('/emergencies/' + em.id)} style={{
                display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 0.8fr 0.8fr 60px',
                padding: '12px 16px', borderBottom: i < arr.length - 1 ? '0.5px solid var(--color-border-light)' : 'none',
                cursor: 'pointer', alignItems: 'center',
                background: em.status === 'active' ? 'var(--color-danger-light)' : 'transparent',
              }}>
                <div style={{ fontSize: 13, color: em.status === 'active' ? 'var(--color-danger)' : 'var(--color-text-primary)' }}>{em.triggeredByName || '—'}</div>
                <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{formatDateTime(em.triggeredAt)}</div>
                <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{triggerLabel(em.triggerType)}</div>
                <div>
                  {em.call999FiredAt ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, color: 'var(--color-danger)' }}>
                      <IconPhone size={11} stroke={1.5} /> Yes
                    </span>
                  ) : <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>No</span>}
                </div>
                <StatusPill status={em.status || 'resolved'} />
                <div style={{ fontSize: 12, color: 'var(--color-brand)', textAlign: 'right' }}>View</div>
              </div>
            ))}
          </Card>
        )}

      </div>
    </Layout>
  );
}